import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'next-i18next'

const parse = (str) => {
  const m = (str || "").match(/^[\d.,\s]*/);
  const num = parseInt((m?.[0] || "").replace(/[.,\s]/g, ""), 10) || 0;
  return { num, suffix: (str || "").slice((m?.[0] || "").length).trim() };
};

const ProgramStatistics = () => {
  const { t } = useTranslation()
  const ref = useRef(null)
  const [inView, setInView] = useState(false)
  const [counts, setCounts] = useState([0, 0, 0, 0])

  const stats = useMemo(() => [
    { ...parse(t("home-page.statistics-stat-1-number")), description: t("home-page.statistics-stat-1-description") },
    { ...parse(t("home-page.statistics-stat-2-number")), description: t("home-page.statistics-stat-2-description") },
    { ...parse(t("home-page.statistics-stat-3-number")), description: t("home-page.statistics-stat-3-description") },
    { ...parse(t("home-page.statistics-stat-4-number")), description: t("home-page.statistics-stat-4-description") },
  ], [t])

  useEffect(() => {
    if (!ref.current) return
    const obs = new IntersectionObserver(([e]) => e.isIntersecting && setInView(true), { threshold: 0.2 })
    obs.observe(ref.current)
    return () => obs.disconnect()
  }, [])

  useEffect(() => {
    if (!inView) return
    const targets = stats.map((s) => s.num)
    const start = Date.now()
    const id = setInterval(() => {
      const p = Math.min((Date.now() - start) / 2000, 1)
      const ease = 1 - (1 - p) ** 4
      setCounts(targets.map((n) => Math.floor(ease * n)))
      if (p >= 1) clearInterval(id)
    }, 16)
    return () => clearInterval(id)
  }, [inView, stats])

  return (
    <section ref={ref}>
      <div className="max-w-[90%] mx-auto flex flex-col items-center mb-[80px] md:mb-[120px]">
        <div className="flex flex-col lg:flex-row items-center justify-center gap-[12px] lg:gap-[100px] mb-[32px] md:mb-[60px] w-full text-center lg:text-left">
          <div className="flex flex-col">
            <h2 className="text-[32px] md:text-[48px] lg:text-[64px] xl:text-[80px] font-[500] text-transparent bg-clip-text bg-gradient-to-b from-[#091F59] to-[#1A3884] leading-tight">
              {t("home-page.statistics-title-1")}
            </h2>
            <h2 className="text-[32px] md:text-[48px] lg:text-[64px] xl:text-[80px] font-[500] text-transparent bg-clip-text bg-gradient-to-b from-[#091F59] to-[#1A3884] leading-tight">
              {t("home-page.statistics-title-2")}
            </h2>
          </div>
          <p className="text-[14px] md:text-[16px] lg:text-[20px] font-[400] max-w-md lg:max-w-[400px] leading-relaxed">
            {t("home-page.statistics-description")}
          </p>
        </div>
        <div className="bg-white rounded-[32px] md:rounded-[48px] p-[40px] w-full lg:w-fit border">
          <div className="flex flex-col lg:flex-row justify-center items-center md:gap-[70px] gap-[95px]">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center max-w-[290px]"
              >
                <div className="text-[32px] font-[600] text-main-pink mb-[12px] md:mb-[16px]">
                  {(counts[index] ?? 0).toLocaleString()}{stat.suffix}
                </div>
                <p className="text-[16px] font-[400] text-gray-700 leading-relaxed">
                  {stat.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default ProgramStatistics
